/**
 * InfinitSales - useFeatureFlags
 *
 * Flags de feature do backend (GET /api/system/features) para gate condicional
 * da UI lead-aware. Enquanto carrega, ou se falhar, todas as flags sao FALSE
 * (a UI nova so aparece quando a flag e confirmada ON; nunca "pisca" ligada por
 * engano). Cache de modulo: 1 fetch por sessao, compartilhado entre todos os
 * consumidores (dedup de requisicoes em voo).
 *
 * Duas adicoes da frente de performance (2026-09-22):
 *  - override de desenvolvimento: fora de producao, o JSON em
 *    localStorage['infinitsales:features_override'] e mesclado por cima do que
 *    o backend devolveu (o backend local nao tem as flags novas ligadas).
 *  - copia persistida (localStorage['infinitsales:features_cache']) da ultima
 *    resposta com sucesso, lida SO por getBootFlag(): serve para decisoes que
 *    acontecem antes do fetch (a autenticacao otimista, no boot do app). O
 *    hook continua partindo de "tudo off" ate o backend confirmar.
 */
import { useEffect, useState } from 'react';
import api from '../services/global/api';

const OVERRIDE_KEY = 'infinitsales:features_override';
const PERSISTED_KEY = 'infinitsales:features_cache';
const IS_DEV = process.env.NODE_ENV !== 'production';

let _cache = null;      // objeto de flags ja carregado (sucesso)
let _inflight = null;   // promise em voo (dedup entre consumidores)

function readJson(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Override de desenvolvimento (nunca em producao). */
function devOverride() {
  if (!IS_DEV) return null;
  const parsed = readJson(OVERRIDE_KEY);
  return parsed && typeof parsed === 'object' ? parsed : null;
}

function withOverride(flags) {
  const override = devOverride();
  return override ? { ...(flags || {}), ...override } : (flags || {});
}

async function loadFlags() {
  if (_cache) return _cache;
  if (!_inflight) {
    _inflight = api
      .get('/api/system/features')
      .then((res) => {
        const fromBackend = (res && res.data && res.data.data) || {};
        try { localStorage.setItem(PERSISTED_KEY, JSON.stringify(fromBackend)); } catch { /* ignore */ }
        _cache = withOverride(fromBackend);
        return _cache;
      })
      .catch(() => withOverride({})) // falha de rede/404 = tudo off (nao mostra UI lead-aware)
      .finally(() => {
        _inflight = null;
      });
  }
  return _inflight;
}

/** Dispara o fetch das flags cedo (boot), sem esperar um consumidor montar. */
export function preloadFeatureFlags() {
  loadFlags();
}

/**
 * Leitura SINCRONA de uma flag ja carregada, para codigo fora de componente
 * (services). Enquanto as flags nao chegaram, devolve false: o chamador segue
 * no comportamento antigo. O override de desenvolvimento vale mesmo antes do
 * fetch, para testar caminhos que decidem na montagem.
 */
export function getFeatureFlag(name) {
  if (_cache) return Boolean(_cache[name]);
  const override = devOverride();
  return Boolean(override && override[name]);
}

/**
 * Flag para decisoes de BOOT (antes de qualquer fetch): usa a copia persistida
 * da ultima resposta do backend, ou o override de desenvolvimento. Uma flag
 * ligada aqui so passa a valer a partir do segundo carregamento da pagina.
 */
export function getBootFlag(name) {
  const override = devOverride();
  if (override && name in override) return Boolean(override[name]);
  if (_cache) return Boolean(_cache[name]);
  const persisted = readJson(PERSISTED_KEY);
  return Boolean(persisted && persisted[name]);
}

export default function useFeatureFlags() {
  const [flags, setFlags] = useState(_cache || withOverride({}));
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    let alive = true;
    if (_cache) {
      setFlags(_cache);
      setLoading(false);
      return undefined;
    }
    loadFlags().then((f) => {
      if (!alive) return;
      setFlags(f || {});
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, []);

  const isEnabled = (name) => Boolean(flags && flags[name]);
  return { flags, loading, isEnabled };
}
